import React, { Component } from "react";
import { withCookies, Cookies } from "react-cookie";
import { instanceOf } from "prop-types";
import { NotificationManager } from "react-notifications";
import { TaskQueue } from 'cwait';
import {
  FiCalendar,
  FiFileText,
  FiCheckCircle,
  FiHome,
  FiMonitor,
  FiUser,
  FiGlobe,
  FiAirplay,
} from 'react-icons/fi';

// Services
import sequencesService from "@services/sequences";
import tpvsService from "@services/tpvs";
import atService from "@services/at";
import apolloService from "@services/apollo";

// Components
import Menu from "@components/Menu";
import Modal from '@components/Modal';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

/* Import Components */
import EditableTable from "@components/EditableTable";
import Button from "@components/Button";
import Input from "@components/Input";
import AtLogin from "@components/AtLogin";
import EmpresaSelect from "@components/EmpresaSelect";
import Checkbox from "@components/CheckBox";
import TestEnvironmentAlert from "@components/TestEnvironmentAlert";

import { EventBus as eventBus } from "@components/EventBus";

import UserContext from "@components/context/userContext";

import "./bulk.css";

const MAX_SIMULTANEOUS_DOWNLOADS = 10;

const zeroPad = (num, places) => String(num).padStart(places, "0");

function getDocType(doctype) {
  switch (doctype) {
    case "FT":
      return "Faturas";
    case "FT_epos":
      return "Faturas (ePOS)";
    case "FT_pos":
      return "Faturas (POS online)";
    case "FT_opt":
      return "Faturas (OPT)";
    case "FR":
      return "Vendas a Dinheiro";
    case "FR_epos":
      return "Vendas a Dinheiro (ePOS)";
    case "NC":
      return "Notas de Crédito";
    case "NC_epos":
      return "Notas de Crédito (ePOS)";
    case "RG":
      return "Recibos";
    case "RG_epos":
      return "Recibos (ePOS)";
    case "OU":
      return "Serviços Internos";
    case "GT":
        return "Guias Transporte";
    case "GT_epos":
      return "Guias Transporte (ePOS)";
    case "GA":
      return "Guias de Ativos";
    default:
      return "Talões";
  }
}

function getATMapDocType(doctype) {
  switch (doctype) {
    case "FT":
    case "FT_epos":
    case "FT_pos":
    case "FT_opt":
      return {
        tipo: "FT",
        classe: "SI",
      };
    case "FR":
    case "FR_epos":
      return {
        tipo: "FR",
        classe: "SI",
      };
    case "NC":
    case "NC_epos":
      return {
        tipo: "NC",
        classe: "SI",
      };
    case "RG":
    case "RG_epos":
      return {
        tipo: "RG",
        classe: "PY",
      };
    case "OU":
      return {
        tipo: "OU",
        classe: "WD",
      };
    case "GT":
    case "GT_epos":
      return {
        tipo: "GT",
        classe: "MG",
      };
    case "GA":
        return {
            tipo: "GA",
            classe: "MG",
        };
    default:
      return {
        tipo: "FT",
        classe: "SI",
      };
  }
}

function categorize(string){
  if (string.includes('EPOSFT')){
    return 'EPOSFT';
  }
  if (string.includes('EPOSFR')){
    return 'EPOSFR';
  }
  if (string.includes('EPOSRG')){
    return 'EPOSRG';
  }
  if (string.includes('EPOSNC')){
    return 'EPOSNC';
  }
  if (string.includes('EPOSGT')){
    return 'EPOSGT';
  }
  if (string.includes('EPOSF')){
    return 'EPOSF';
  }
  if (string.includes('EPOSOPT')){
    return 'EPOSOPT';
  }
  if (string.includes('FT')){
    return 'FT';
  }
  if (string.includes('NC')){
    return 'NC';
  }
  if (string.includes('FR')){
    return 'FR';
  }
  if (string.includes('RG')){
    return 'RG';
  }
  if (string.includes('GT')){
    return 'GT';
  }
  if (string.includes('F')){
    return 'F';
  }
}

function nextSerie(serie, isbackoffice) {
  const prefix = categorize(serie);
  if (isbackoffice) {
    return `${prefix}${zeroPad(parseInt(serie.replace(prefix, ""), 10) + 1, 2)}`;
  }
  return `${prefix}${parseInt(serie.replace(prefix, ""), 10) + 1}`;
}

function nextInitial(startdate, isbackoffice, doctype) {
  const currentYear = startdate.substring(0, 4);
  if (isbackoffice) {
    if (doctype.includes('_epos')) {
      return 1;
    }
    return `${currentYear}000001`;
  }
  return 1;
}

class Form extends Component {
  static contextType = UserContext;
  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);

    const {
      match: { params },
      type,
    } = this.props;

    const startDate = new Date();

    this.state = {
      form: {
        next: "",
        previous: "",
        loading: {
          save: false,
        },
        avoidSubmit: false,
      },
      loaded: false,
      name: "",
      nif: "",
      email: "",
      phone: "",
      address: "",
      idapollo: "",
      idprovider: type === "edit" && params.idprovider,
      edit: type === "edit",
      data: [],
      dataChanged: -1,
      tpvs: [],
      atlogin: false,
      date: startDate.toISOString().substr(0, 10),
      environment: 'production',
      allSelected: false,
      activeFilter: null, // null = todos, 'POS', 'OPT', 'TPV', 'EPOS'
    };
    this.preHandleFormSubmit = this.preHandleFormSubmit.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleFormCancel = this.handleFormCancel.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.loadFunction = this.loadFunction.bind(this);
    this.onTableChange = this.onTableChange.bind(this);
    this.onLogin = this.onLogin.bind(this);
    this.validForm = this.validForm.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
  }

  async loadFunction() {
    if (!this.state.loaded) {
      const profile = this.context;

      try {
        // Load TPVs and sequences in parallel
        const [tpvsData, sequencesData] = await Promise.all([
          tpvsService.getBulk(profile.idEmpresaSec, this.state.environment),
          sequencesService.get(profile.idEmpresaSec, null, this.state.environment)
        ]);

        // Ensure tpvsData is an array
        const tpvsArray = Array.isArray(tpvsData) ? tpvsData : [];

        const enrichedTpvs = tpvsArray.map((tpv, index) => {
          const { tipo, classe } = getATMapDocType(tpv.doctype);
          
          // Para FT_opt, garantir que a série segue o padrão EPOSOPT1, EPOSOPT2, etc.
          let serie = tpv.serie;
          if (tpv.doctype === 'FT_opt') {
            // Se não tem série ou não começa com EPOSOPT, começar com EPOSOPT1
            if (!serie || !serie.includes('EPOSOPT')) {
              serie = 'EPOSOPT1';
            } else {
              // Se já tem EPOSOPT, usar nextSerie para incrementar
              serie = nextSerie(serie, tpv.posto === '');
            }
          } else {
            // Para outros tipos, usar a lógica normal
            serie = nextSerie(tpv.serie, tpv.posto === '');
          }
          
          return {
            ...tpv,
            id: index,
            serie: serie,
            initial: nextInitial(this.state.date, tpv.posto === '', tpv.doctype),
            doctypedesc: getDocType(tpv.doctype),
            tipo,
            classe,
          };
        });

        const enrichedTpvsByDocumentType = enrichedTpvs.reduce((acc, item) => {
          // Tratar posto vazio, null, undefined, '0' ou 0 como backoffice
          const postoValue = item.posto;
          const isBackoffice = !postoValue || postoValue === '' || postoValue === '0' || postoValue === 0;
          
          if (!isBackoffice) {
            if (!acc[postoValue]) {
              acc[postoValue] = { name: item.nome, selected: false, disabled: false, childs: {} };
            }
            if (!acc[postoValue].childs[item.tpv]) {
              acc[postoValue].childs[item.tpv] = { selected: false, disabled: false, name: item.tpv, items: [] };
            }
            acc[postoValue].childs[item.tpv].items.push({
              ...item,
              id: `${postoValue}|${item.tpv}|${item.index}`
            });
          } else {
            // Garantir que só existe um backoffice
            if (!acc.bo) {
              acc.bo = { name: 'Backoffice', selected: false, disabled: false, childs: {} };
            }
            if (!acc.bo.childs[item.doctype]) {
              // Para o Backoffice, usar nomes mais curtos para FT_pos e FT_opt
              let displayName = getDocType(item.doctype);
              if (item.doctype === 'FT_pos') {
                displayName = 'POS Online';
              } else if (item.doctype === 'FT_opt') {
                displayName = 'OPT';
              }
              acc.bo.childs[item.doctype] = { selected: false, disabled: false, name: displayName, items: [] };
            }
            acc.bo.childs[item.doctype].items.push({
              ...item,
              id: `bo|${item.doctype}|${item.index}`
            });
          }
          return acc;
        }, {});

        this.setState({ 
          tpvs: enrichedTpvsByDocumentType, 
          sequences: sequencesData, 
          loaded: true 
        });
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    }
  }

  preHandleFormSubmit(e) {
    const { form } = this.state;
    e.preventDefault();

    eventBus.$emit("Modal-dateToSeries", {
      header: "Data de ínicio",
    })
  }

  async handleFormSubmit() {
    const { history } = this.props;
    const { tpvs: localTpvs, date } = this.state;

    confirmAlert({
      title: 'Confirmação',
      message: `Confirma que deseja criar séries automáticas para o dia ${date}?`,
      buttons: [
          {
              label: 'Sim',
              onClick: async () => {
                eventBus.$emit("Modal-dateToSeries", { isOpen: false })
                this.setState({
                  form: {
                    ...this.state.form,
                    loading: {
                      ...this.state.loading,
                      save: true,
                    },
                    avoidSubmit: true,
                  },
                  loaded: true,
                });
            
                const profile = this.context;
            
                const queue = new TaskQueue(Promise, MAX_SIMULTANEOUS_DOWNLOADS);
            
                let tpvs = [];

                Object.keys(localTpvs).map((posto) => {
                  Object.keys(localTpvs[posto].childs).map((tpv) => {
                    if (localTpvs[posto].childs[tpv].selected) {
                      tpvs = [
                        ...tpvs,
                        ...localTpvs[posto].childs[tpv].items
                      ];
                    }
                  })
                })

                await Promise.all(
                  tpvs.map(queue.wrap(async (tpv, index) => {
                    const { tipo, classe } = getATMapDocType(tpv.doctype);
                    try {
                      const seriesData = {
                        idempresa: profile.idEmpresaSec,
                        environment: this.state.environment,
                        posto: tpv.posto,
                        tpv: tpv.tpv,
                        serie: tpv.serie,
                        numInicialSeq: tpv.initial,
                        startAt: date,
                        doctype: tpv.doctype,
                        data: {
                          serie: `${tpv.posto}${tpv.tpv}${tpv.serie}`,
                          tipoSerie: "N",
                          classeDoc: classe,
                          tipoDoc: tipo,
                          numInicialSeq: tpv.initial,
                          dataInicioPrevUtiliz: date,
                          numCertSWFatur: 942,
                          meioProcessamento: "PI",
                        },
                      };
                      return await atService.registerSeries(seriesData);
                    } catch (error) {
                      console.error('Erro ao registar série:', error);
                      return 'NOK';
                    }
                  }))
                )
                  .then((responses) => {
                    responses.map((response = 'NOK', index) => {
                      const tpv = tpvs[index];
                      let statusResponse = "";
                      let atcud = "";
                      if (response !== "NOK" && response.success) {
                        NotificationManager.success(
                          "Submetida com sucesso para a AT",
                          `${tpv.posto}${tpv.tpv}${tpv.serie}`,
                          100,
                          );
                        statusResponse = (
                          <span className="badge badge-success">SUCESSO</span>
                        );
                        atcud = response.data;
                      } else {
                        if (response && response.status === 4001) {
                          NotificationManager.warning(
                            "Série já existente",
                            `${tpv.posto}${tpv.tpv}${tpv.serie}`,
                            1000,
                          );
                          statusResponse = (
                            <span className="badge badge-warning">Repetida</span>
                          );
                        } else {
                          NotificationManager.error(
                            "Não submetida para a AT",
                            `${tpv.posto}${tpv.tpv}${tpv.serie}`,
                            1000,
                          );
                          statusResponse = JSON.stringify(response);
                        }
                      }
                      // Determinar a chave do posto
                      const postoValue = tpv.posto;
                      const isBackoffice = !postoValue || postoValue === '' || postoValue === '0' || postoValue === 0;
                      const postoKey = isBackoffice ? 'bo' : postoValue;
                      
                      // Determinar a chave do child
                      // Para backoffice, agrupa por doctype; para postos normais, por tpv
                      const childKey = isBackoffice ? tpv.doctype : (tpv.tpv || tpv.doctype);
                      
                      // Verificar se o posto e o child existem antes de aceder
                      if (localTpvs[postoKey] && localTpvs[postoKey].childs && localTpvs[postoKey].childs[childKey]) {
                        localTpvs[postoKey].childs[childKey].items =
                          localTpvs[postoKey].childs[childKey].items.map((tpvAux) => {
                            if (tpv.doctype === tpvAux.doctype && 
                                tpv.posto === tpvAux.posto && 
                                tpv.tpv === tpvAux.tpv &&
                                tpv.serie === tpvAux.serie) {
                              return {
                                ...tpvAux,
                                result: statusResponse,
                                atcud,
                                date,
                              };
                            } else {
                              return tpvAux;
                            }
                          });
                      }

                      this.setState({
                        tpvs: localTpvs,
                        form: {
                          ...this.state.form,
                          loading: {
                            ...this.state.loading,
                            save: false,
                          },
                        },
                      });
                    });
                  })
                  .finally(async () => {
                    const { tpvs: localTpvs } = this.state;

                    let tpvs = [];

                    Object.keys(localTpvs).map((posto) => {
                      Object.keys(localTpvs[posto].childs).map((tpv) => {
                        if (localTpvs[posto].childs[tpv].selected) {
                          tpvs = [
                            ...tpvs,
                            ...localTpvs[posto].childs[tpv].items
                          ];
                        }
                      })
                    })
            
                    try {
                      const syncData = {
                        idempresa: profile.idEmpresaSec,
                        series: tpvs
                          .filter(({ atcud }) => atcud)
                          .map((item) => ({
                            ...item,
                            result: "",
                          })),
                      };
                      
                      await apolloService.syncSeries(syncData);
                      eventBus.$emit("Modal-archive", { header: "Séries geradas com sucesso!", body: (
                        <div className="row" style={{ textAlign: 'center' }}>
                            <Button
                                action={() => window.location.href = 'https://eposgestofinal.com/index.php?page=downloads&path=EnviadoServidor'}
                                type={"info"}
                                title={"Download ficheiros gerados"}
                                style={buttonStyle}
                            />
                        </div>
                      ) })
                    } catch (error) {
                      console.error('Erro ao sincronizar séries:', error);
                    }
                  });
              },
          },
          {
              label: 'Não',
              onClick: () => {}
          }
      ]
    });
  }

  handleFormCancel() {
    const { history } = this.props;
    history.push("/series");
  }

  handleChange(e) {
    let value = e.target.value;
    let name = e.target.name;

    if (name === "date") {
      this.setState(
        {
          [name]: value,
        },
        () => {
          const { tpvs: localTpvs } = this.state;
          
          // Iterar através de todas as estruturas aninhadas
          // Apenas atualizar o 'initial', não o nome da série
          Object.keys(localTpvs).forEach((postoKey) => {
            Object.keys(localTpvs[postoKey].childs).forEach((tpvKey) => {
              localTpvs[postoKey].childs[tpvKey].items = localTpvs[postoKey].childs[tpvKey].items.map((tpv) => ({
                ...tpv,
                initial: nextInitial(this.state.date, tpv.posto === '', tpv.doctype)
              }));
            });
          });
          
          this.setState({ tpvs: localTpvs });
        }
      );
      return;
    }

    this.setState({
      [name]: value,
    });
  }

  handleCheckboxChange(e, callback) {
    const { tpvs: localTpvs } = this.state;
    let value = e.target.checked;
    let name = e.target.name;

    const pathToTpv = name.split('|');

    if (pathToTpv.length === 2) {
      localTpvs[pathToTpv[1]].selected = value;
      Object.keys(localTpvs[pathToTpv[1]].childs).map((tpv) => {
        localTpvs[pathToTpv[1]].childs[tpv].selected = value;
        localTpvs[pathToTpv[1]].childs[tpv].disabled = value;
      })
      this.setState({
        [name]: value,
        tpvs: localTpvs,
      });
    } else if (pathToTpv.length === 3) {
      localTpvs[pathToTpv[1]].childs[pathToTpv[2]].selected = value;
      this.setState({
        [name]: value,
        tpvs: localTpvs,
      });
    } else if (name === 'allSelected') {
      Object.keys(localTpvs).map((posto) => {
        localTpvs[posto].selected = value;
        localTpvs[posto].disabled = value;
        Object.keys(localTpvs[posto].childs).map((tpv) => {
          localTpvs[posto].childs[tpv].selected = value;
          localTpvs[posto].childs[tpv].disabled = value;
        })
      })
      this.setState({
        [name]: value,
        tpvs: localTpvs,
      });
    } else {
      this.setState({
          [name]: value,
      });
    }
  };

  onTableChange(newValue, row, column) {
    const { tpvs: localTpvs } = this.state;

    const [posto, tpv, index] = row.id.split('|');

    localTpvs[posto].childs[tpv].items = localTpvs[posto].childs[tpv].items.map((value) => {
      if (value.id === index) {
        const newRow = { ...value };
        newRow[column.dataField] = newValue;
        return newRow;
      }
      return value;
    })
    
    this.setState({ tpvs: localTpvs });
    return true;
  }

  onLogin(loginResult) {
    this.setState({
      atlogin: true,
      environment: loginResult.environment
    });
    this.loadFunction();
  }

  validForm() {
    const { tpvs: localTpvs } = this.state;
    let tpvs = [];

    Object.keys(localTpvs).map((posto) => {
      Object.keys(localTpvs[posto].childs).map((tpv) => {
        if (localTpvs[posto].childs[tpv].selected) {
          tpvs = [
            ...tpvs,
            ...localTpvs[posto].childs[tpv].items
          ];
        }
      })
    })

    return tpvs.length === 0;
  }

  handleFilterChange(filter) {
    this.setState({ activeFilter: filter === this.state.activeFilter ? null : filter });
  }

  shouldShowItem(item, tpv, isBackoffice = false) {
    const { activeFilter } = this.state;
    
    if (!activeFilter) return true;
    
    // No Backoffice, o tpv é na verdade o doctype
    if (isBackoffice) {
      switch (activeFilter) {
        case 'POS':
          return item.doctype === 'FT_pos';
        case 'OPT':
          return item.doctype === 'FT_opt';
        case 'TPV':
          return false; // Backoffice não tem TPVs normais
        case 'EPOS':
          return item.doctype && item.doctype.includes('_epos');
        default:
          return true;
      }
    }
    
    // Para postos normais, usar a lógica baseada em tpv
    switch (activeFilter) {
      case 'POS':
        return String(tpv) === '0';
      case 'OPT':
        return String(tpv) === '-1';
      case 'TPV':
        return String(tpv) !== '0' && String(tpv) !== '-1';
      case 'EPOS':
        return item.doctype && item.doctype.includes('_epos');
      default:
        return true;
    }
  }

  render() {
    const profile = this.context;
    const { location, history } = this.props;
    const { loaded, tpvs, atlogin, form, allSelected, activeFilter } = this.state;
    const cookies = new URLSearchParams(location.search);

    return (
      <div className="bulk-sequences-page">
        <Menu
          location={location}
          history={history}
          newCookies={cookies}
        />
        
        {/* Breadcrumb */}
        <div className="fullContainer" style={{ maxWidth: "100%" }}>
          <div className="col-xs-12">
            <div className="breadcrumb-wrapper">
              <ol
                style={{
                  textAlign: "left",
                  marginTop: "0",
                }}
                className="breadcrumb"
              >
                <li>
                  <a href="http://eposgestofinal.com/index.php">Portal</a>
                </li>
                <li>
                  <a href="/series">Séries</a>
                </li>
                <li className="active">Automáticas</li>
              </ol>
            </div>
          </div>
        </div>

        <TestEnvironmentAlert />
        <EmpresaSelect />
        <div className="panel panel-default" style={{ marginTop: '20px' }}>
            <div className="panel-heading">
                <h3 className="panel-title" style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                🔐 Login AT (Obrigatório)
                </h3>
            </div>
            <div className="panel-body">
                <AtLogin
                onLogin={this.onLogin}
                selectedEmpresas={true}
                />
            </div>
        </div>
        {loaded && atlogin && (
          <div className="bulk-sequences-container">
            <div className="main-content-panel">
              {/* Header */}
              <div className="main-panel-header">
                <h1 className="main-panel-title">
                  <FiFileText className="main-panel-title-icon" />
                  Novas Séries Automáticas
                </h1>
              </div>

              {/* Date Section */}
              <div className="date-section">
                <div className="date-section-content">
                  <div className="date-section-icon">
                    <FiCalendar />
                  </div>
                  <div className="date-section-info">
                    <label className="date-section-label">Data de Início</label>
                    <div className="date-input-wrapper">
                      <Input
                        name={"date"}
                        inputType={"date"}
                        value={this.state.date}
                        handleChange={this.handleChange}
                      />
                    </div>
                    <div className="date-section-description">
                      Selecione a data a partir da qual as séries serão válidas
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter Buttons */}
              <div style={{
                display: 'flex',
                gap: '15px',
                marginBottom: '30px',
                padding: '20px',
                background: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                <button
                  type="button"
                  onClick={() => this.handleFilterChange('POS')}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: activeFilter === 'POS' ? '#007bff' : '#dee2e6',
                    background: activeFilter === 'POS' ? '#007bff' : 'white',
                    color: activeFilter === 'POS' ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeFilter !== 'POS') {
                      e.target.style.borderColor = '#007bff';
                      e.target.style.color = '#007bff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFilter !== 'POS') {
                      e.target.style.borderColor = '#dee2e6';
                      e.target.style.color = '#495057';
                    }
                  }}
                >
                  <FiGlobe />
                  POS
                </button>
                <button
                  type="button"
                  onClick={() => this.handleFilterChange('OPT')}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: activeFilter === 'OPT' ? '#007bff' : '#dee2e6',
                    background: activeFilter === 'OPT' ? '#007bff' : 'white',
                    color: activeFilter === 'OPT' ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeFilter !== 'OPT') {
                      e.target.style.borderColor = '#007bff';
                      e.target.style.color = '#007bff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFilter !== 'OPT') {
                      e.target.style.borderColor = '#dee2e6';
                      e.target.style.color = '#495057';
                    }
                  }}
                >
                  <FiAirplay />
                  OPT
                </button>
                <button
                  type="button"
                  onClick={() => this.handleFilterChange('TPV')}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: activeFilter === 'TPV' ? '#007bff' : '#dee2e6',
                    background: activeFilter === 'TPV' ? '#007bff' : 'white',
                    color: activeFilter === 'TPV' ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeFilter !== 'TPV') {
                      e.target.style.borderColor = '#007bff';
                      e.target.style.color = '#007bff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFilter !== 'TPV') {
                      e.target.style.borderColor = '#dee2e6';
                      e.target.style.color = '#495057';
                    }
                  }}
                >
                  <FiMonitor />
                  TPV
                </button>
                <button
                  type="button"
                  onClick={() => this.handleFilterChange('EPOS')}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: activeFilter === 'EPOS' ? '#007bff' : '#dee2e6',
                    background: activeFilter === 'EPOS' ? '#007bff' : 'white',
                    color: activeFilter === 'EPOS' ? 'white' : '#495057',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeFilter !== 'EPOS') {
                      e.target.style.borderColor = '#007bff';
                      e.target.style.color = '#007bff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFilter !== 'EPOS') {
                      e.target.style.borderColor = '#dee2e6';
                      e.target.style.color = '#495057';
                    }
                  }}
                >
                  <FiFileText />
                  EPOS
                </button>
              </div>

              {/* Series Section */}
              <div className="series-section">
                <h2 className="series-section-title">
                  <FiUser />
                  Séries de Documentos
                </h2>
                <form
                  className="series-form"
                  onSubmit={this.preHandleFormSubmit}
                >
                  {/* All Documents Checkbox */}
                  {!activeFilter && (
                    <div className="all-documents-checkbox">
                      <Checkbox
                        position={"left"}
                        name={"allSelected"}
                        checked={allSelected}
                        handleChange={this.handleCheckboxChange}
                      />
                      <h1>
                        <FiCheckCircle className="all-documents-checkbox-icon" />
                        Selecionar Todos os Documentos
                      </h1>
                    </div>
                  )}

                  {/* Postos */}
                  {Object.keys(tpvs)
                    .filter((posto) => {
                      // Se não há filtro ativo, mostrar todos os postos
                      if (!activeFilter) return true;
                      
                      // Verificar se há algum grupo (tpv) visível neste posto após o filtro
                      const postoData = tpvs[posto];
                      const isBackoffice = posto === 'bo';
                      
                      return Object.keys(postoData.childs).some((tpv) => {
                        const tpvData = postoData.childs[tpv];
                        // Verificar se há algum item visível neste grupo
                        return tpvData.items.some(item => this.shouldShowItem(item, tpv, isBackoffice));
                      });
                    })
                    .map((posto) => (
                    <div key={posto} className="posto-card">
                      <div className="posto-header">
                        {!activeFilter && (
                          <Checkbox
                            position={"left"}
                            name={`tpvs|${posto}`}
                            checked={tpvs[posto].selected}
                            disabled={tpvs[posto].disabled}
                            handleChange={this.handleCheckboxChange}
                          />
                        )}
                        <h3 className="posto-title">
                          <FiHome className="posto-title-icon" />
                          {tpvs[posto].name}
                        </h3>
                      </div>
                      <div className="posto-content">
                        {Object.keys(tpvs[posto].childs)
                          .filter((tpv) => {
                            if (!activeFilter) return true;
                            const tpvData = tpvs[posto].childs[tpv];
                            const isBackoffice = posto === 'bo';
                            // Verificar se algum item do grupo corresponde ao filtro
                            return tpvData.items.some(item => this.shouldShowItem(item, tpv, isBackoffice));
                          })
                          .map((tpv) => {
                            // Filtrar items dentro do grupo se necessário
                            const tpvData = tpvs[posto].childs[tpv];
                            const isBackoffice = posto === 'bo';
                            const filteredItems = activeFilter 
                              ? tpvData.items.filter(item => this.shouldShowItem(item, tpv, isBackoffice))
                              : tpvData.items;
                            
                            // Não mostrar o grupo se não houver items após filtro
                            if (filteredItems.length === 0) return null;
                            
                            return (
                          <div key={tpv} className="tpv-section">
                            <div className="tpv-header">
                              <Checkbox
                                position={"left"}
                                name={`tpvs|${posto}|${tpv}`}
                                checked={tpvs[posto].childs[tpv].selected}
                                disabled={tpvs[posto].childs[tpv].disabled}
                                handleChange={this.handleCheckboxChange}
                              />
                              {(() => {
                                const tpvData = tpvs[posto].childs[tpv];
                                const firstItem = tpvData.items[0];
                                
                                // Para Backoffice, verificar o doctype para mostrar o ícone e nome correto
                                if (posto === 'bo') {
                                  if (firstItem && firstItem.doctype === 'FT_opt') {
                                    return (
                                      <h5 className="tpv-title">
                                        <FiAirplay className="tpv-title-icon" />
                                        OPT
                                      </h5>
                                    );
                                  } else if (firstItem && firstItem.doctype === 'FT_pos') {
                                    return (
                                      <h5 className="tpv-title">
                                        <FiGlobe className="tpv-title-icon" />
                                        POS Online
                                      </h5>
                                    );
                                  } else {
                                    return (
                                      <h5 className="tpv-title">
                                        <FiFileText className="tpv-title-icon" />
                                        {tpvData.name}
                                      </h5>
                                    );
                                  }
                                }
                                
                                // Para postos normais, usar a lógica existente
                                if (tpv !== '0' && tpv !== '-1') {
                                  return (
                                    <h5 className="tpv-title">
                                      <FiMonitor className="tpv-title-icon" />
                                      {`TPV: ${tpvData.name}`}
                                    </h5>
                                  );
                                } else if (tpv === '-1') {
                                  return (
                                    <h5 className="tpv-title">
                                      <FiAirplay className="tpv-title-icon" />
                                      OPT
                                    </h5>
                                  );
                                } else {
                                  return (
                                    <h5 className="tpv-title">
                                      <FiGlobe className="tpv-title-icon" />
                                      POS Online
                                    </h5>
                                  );
                                }
                              })()}
                            </div>
                            <EditableTable
                              columns={[
                                {
                                  dataField: "serie",
                                  text: "Série",
                                  validator: this.onTableChange,
                                  editable: true,
                                },
                                {
                                  dataField: "initial",
                                  text: "Inicial",
                                  validator: this.onTableChange,
                                  editable: true,
                                },
                                {
                                  dataField: "tipo",
                                  text: "Tipo",
                                  validator: this.onTableChange,
                                  editable: false,
                                },
                                {
                                  dataField: "classe",
                                  text: "Classe",
                                  validator: this.onTableChange,
                                  editable: false,
                                },
                                {
                                  dataField: "doctypedesc",
                                  text: "Documento",
                                  editable: false,
                                },
                                {
                                  dataField: "tpv",
                                  text: "TPV",
                                  editable: false,
                                  formatter: (_, row) => {
                                    return row.tpv != 0 && row.tpv != -1 ? row.tpv : '';
                                  },
                                },
                                {
                                  dataField: "posto",
                                  text: "Posto",
                                  editable: false,
                                  formatter: (_, row) => {
                                    return row.nome;
                                  },
                                },
                                {
                                  dataField: "result",
                                  text: "Resultado",
                                  editable: false,
                                },
                              ]}
                              data={filteredItems}
                            />
                          </div>
                            );
                          })
                          .filter(item => item !== null)}
                      </div>
                    </div>
                  ))}

                  {/* Actions */}
                  <div className="actions-section">
                    {!form.avoidSubmit && (
                      <Button
                        disabled={this.validForm()}
                        action={this.preHandleFormSubmit}
                        type={"success"}
                        title="Submeter Séries"
                        loading={form.loading.save}
                        style={buttonStyle}
                      />
                    )}
                    <Button
                      action={this.handleFormCancel}
                      type={"secondary"}
                      title="Voltar"
                      style={buttonStyle}
                    />
                  </div>
                </form>
              </div>
            </div>

            {/* Modals */}
            <Modal id="dateToSeries">
              <div className="row">
                <Input
                  name={"date"}
                  inputType={"date"}
                  value={this.state.date}
                  handleChange={this.handleChange}
                />
              </div>
              <div className="row">
                <Button
                  action={this.handleFormSubmit}
                  type={"success"}
                  title={"Submeter"}
                  loading={form.loading.save}
                  style={buttonStyle}
                />
              </div>
            </Modal>
            <Modal id="archive" />
          </div>
        )}
      </div>
    );
  }
}

const buttonStyle = {
  margin: "10px 10px 10px 10px",
};

export default withCookies(Form);
